import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle, ArrowRight, Building2, Check, CheckCircle2, ChevronLeft,
  FileBadge2, ImagePlus, Landmark, Loader2, MapPin, Save, Trash2,
  UploadCloud, UserRound, X,
} from "lucide-react";
import toast from "react-hot-toast";
import axiosInstance from "../utils/axiosInstance";
import { getFormErrors } from "../utils/formErrors";
import { toastApiError, toastErrors } from "../utils/toastErrors";
import { toLatinDigits } from "../utils/numberFormat";

const configs = {
  buyer: {
    title: "خریدار", plural: "خریداران", collection: "buyers", icon: UserRound,
    sections: [
      {
        id: "identity", title: "اطلاعات هویتی", description: "مشخصات خریدار برای درج در اسناد بازرگانی", icon: FileBadge2,
        fields: [
          { name: "buyer_name", label: "نام خریدار", required: true, maxLength: 255, placeholder: "نام شخص یا شرکت خریدار" },
          { name: "buyer_card_number", label: "شماره کارت بازرگانی", required: true, maxLength: 55, dir: "ltr", inputMode: "numeric", placeholder: "0001234567", hint: "شماره را با حفظ صفرهای ابتدایی وارد کنید." },
        ],
      },
      {
        id: "contact", title: "اطلاعات تماس و نشانی", description: "راه ارتباطی و نشانی خریدار", icon: MapPin,
        fields: [
          { name: "buyer_tel", label: "شماره تلفن", maxLength: 55, dir: "ltr", type: "tel", placeholder: "+98 21 1234 5678", autoComplete: "tel" },
          { name: "buyer_address", label: "نشانی خریدار", required: true, maxLength: 555, multiline: true, placeholder: "کشور، شهر و نشانی کامل خریدار", autoComplete: "street-address" },
        ],
      },
    ],
  },
  seller: {
    title: "فروشنده", plural: "فروشندگان", collection: "sellers", icon: Building2,
    sections: [
      {
        id: "identity", title: "اطلاعات فروشنده", description: "نام و نشانی فروشنده مطابق اسناد تجاری", icon: Building2,
        fields: [
          { name: "seller_name", label: "نام فروشنده", required: true, maxLength: 255, placeholder: "نام شخص یا شرکت فروشنده" },
          { name: "seller_country", label: "کشور", maxLength: 55, placeholder: "کشور محل فعالیت فروشنده", autoComplete: "country-name" },
          { name: "seller_address", label: "نشانی فروشنده", required: true, maxLength: 2000, multiline: true, wide: true, placeholder: "شهر و نشانی کامل فروشنده", autoComplete: "street-address" },
        ],
      },
      {
        id: "bank", title: "اطلاعات بانکی", description: "مشخصات حساب فروشنده برای استفاده در اسناد", icon: Landmark,
        fields: [
          { name: "seller_bank_name", label: "نام بانک", maxLength: 55, placeholder: "نام بانک فروشنده" },
          { name: "seller_account_name", label: "نام صاحب حساب", maxLength: 255, placeholder: "نام صاحب حساب مطابق اطلاعات بانکی" },
          { name: "seller_iban", label: "شماره IBAN", maxLength: 55, dir: "ltr", placeholder: "IR00 0000 0000 0000 0000 0000 00", hint: "حروف و اعداد را مطابق اطلاعات بانک وارد کنید." },
          { name: "seller_swift", label: "کد SWIFT", maxLength: 55, dir: "ltr", placeholder: "XXXXXXXX", hint: "کد شناسایی بانک فروشنده" },
        ],
      },
      {
        id: "images", title: "لوگو و مهر", description: "تصاویر فروشنده برای استفاده در اسناد بازرگانی", icon: ImagePlus,
        fields: [
          { name: "seller_logo", label: "لوگوی فروشنده", type: "file" },
          { name: "seller_seal", label: "مهر فروشنده", type: "file" },
        ],
      },
    ],
  },
};

const emptyErrors = () => ({ fields: {}, general: "" });
const getFields = (config) => config.sections.flatMap((section) => section.fields);
const getValues = (fields, data = {}) => Object.fromEntries(fields.map((field) => [
  field.name, field.type === "file" ? null : String(data[field.name] ?? ""),
]));
const getFiles = (fields, data) => Object.fromEntries(fields.filter((field) => field.type === "file").map((field) => [field.name, data[field.name] || ""]));

export default function PartyForm({ type, recordId }) {
  const config = configs[type];
  const { title, plural, collection, icon: PartyIcon } = config;
  const fields = getFields(config);
  const editing = recordId != null;
  const navigate = useNavigate();
  const formRef = useRef(null);
  const pending = useRef(false);
  const [values, setValues] = useState(() => getValues(fields));
  const [initialValues, setInitialValues] = useState(() => getValues(fields));
  const [savedFiles, setSavedFiles] = useState({});
  const [loading, setLoading] = useState(editing);
  const [loadError, setLoadError] = useState("");
  const [retry, setRetry] = useState(0);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [errors, setErrors] = useState(emptyErrors);
  const [saved, setSaved] = useState(false);
  const busy = saving || deleting;
  const dirty = fields.some((field) => values[field.name] !== initialValues[field.name]);
  const displayName = values[`${type}_name`]?.trim() || `${title} جدید`;

  useEffect(() => {
    let active = true;
    const currentFields = getFields(configs[type]);
    setErrors(emptyErrors());
    setSaved(false);
    setLoadError("");
    if (!editing) {
      const initial = getValues(currentFields);
      setValues(initial);
      setInitialValues(initial);
      setSavedFiles({});
      setLoading(false);
      return () => { active = false; };
    }
    setLoading(true);
    axiosInstance.get(`/documents/${collection}/by-id/${recordId}/`)
      .then(({ data }) => {
        if (!active) return;
        const initial = getValues(currentFields, data);
        setValues(initial);
        setInitialValues(initial);
        setSavedFiles(getFiles(currentFields, data));
      })
      .catch(() => {
        if (!active) return;
        setLoadError(`اطلاعات ${title} دریافت نشد. لطفاً دوباره تلاش کنید.`);
        toast.error(`اطلاعات ${title} دریافت نشد.`);
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [type, collection, title, recordId, editing, retry]);

  useEffect(() => {
    if (errors.general && !busy) {
      (formRef.current?.querySelector('[aria-invalid="true"]') || formRef.current?.querySelector('[role="alert"]'))?.focus();
    }
  }, [errors, busy]);

  const update = (field, event) => {
    const value = field.type === "file" ? event.target.files?.[0] || null
      : field.dir === "ltr" ? toLatinDigits(event.target.value) : event.target.value;
    setValues((current) => ({ ...current, [field.name]: value }));
    setSaved(false);
    setErrors((current) => ({ ...current, general: "", fields: { ...current.fields, [field.name]: "" } }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (pending.current || (editing && !dirty)) return;
    const missing = fields.filter((field) => field.required && field.type !== "file" && !String(values[field.name] ?? "").trim());
    if (missing.length) {
      const required = Object.fromEntries(missing.map((field) => [field.name, "الزامی است."]));
      setErrors({ fields: required, general: "لطفاً موارد مشخص‌شده را اصلاح کنید." });
      toastErrors(missing.map((field) => `${field.label}: الزامی است.`));
      return;
    }
    pending.current = true;
    setSaving(true);
    setSaved(false);
    setErrors(emptyErrors());
    try {
      let payload = values;
      const options = [];
      if (type === "seller") {
        payload = new FormData();
        fields.forEach((field) => {
          const value = values[field.name];
          // Omit existing image URLs so an unchanged upload stays on the server.
          if (field.type !== "file" || value instanceof File) payload.append(field.name, value);
        });
        options.push({ headers: { "Content-Type": "multipart/form-data" } });
      }
      const { data } = editing
        ? await axiosInstance.put(`/documents/${collection}/${recordId}/`, payload, ...options)
        : await axiosInstance.post(`documents/${collection}/`, payload, ...options);
      if (editing) {
        const initial = getValues(fields, { ...values, ...data });
        setValues(initial);
        setInitialValues(initial);
        setSavedFiles((previous) => getFiles(fields, { ...previous, ...data }));
        formRef.current?.querySelectorAll('input[type="file"]').forEach((input) => { input.value = ""; });
        setSaved(true);
      } else {
        toast.success(`${title} با موفقیت ایجاد شد.`);
        navigate(data?.id ? `/${collection}/details/${data.id}` : `/${collection}/list`);
      }
    } catch (error) {
      setErrors(getFormErrors(error, fields.map((field) => field.name)));
      toastApiError(
        error,
        Object.fromEntries(fields.map((field) => [field.name, field.label])),
        `ذخیره ${title} انجام نشد. لطفاً دوباره تلاش کنید.`,
      );
    } finally {
      pending.current = false;
      setSaving(false);
    }
  };

  const deleteRecord = async () => {
    if (pending.current) return;
    pending.current = true;
    setDeleting(true);
    setErrors(emptyErrors());
    try {
      await axiosInstance.delete(`/documents/${collection}/${recordId}/`);
      toast.success(`${title} حذف شد.`);
      navigate(`/${collection}/list`);
    } catch (error) {
      setDeleteOpen(false);
      setErrors(getFormErrors(error, [], `حذف ${title} انجام نشد. لطفاً دوباره تلاش کنید.`));
      toastApiError(error, {}, `حذف ${title} انجام نشد. لطفاً دوباره تلاش کنید.`);
    } finally {
      pending.current = false;
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-3 pb-10 pt-24 font-vazir sm:px-6 lg:px-8" dir="rtl">
      <div className="mx-auto max-w-6xl">
        <nav aria-label="مسیر صفحه" className="mb-5 flex flex-wrap items-center gap-2 text-xs text-slate-500 sm:text-sm">
          <Link to={`/${collection}/list`} onClick={(event) => { if (busy) event.preventDefault(); }} className="rounded hover:text-blue-700 focus-visible:outline-blue-500">{plural}</Link>
          <ChevronLeft aria-hidden="true" size={14} />
          <span aria-current="page" className="text-slate-700">{editing ? `ویرایش ${title}` : `${title} جدید`}</span>
        </nav>
        <header className="mb-7 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 sm:gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-900 text-white"><PartyIcon aria-hidden="true" size={24} /></span>
            <div className="min-w-0">
              <h1 className="text-xl font-bold tracking-tight text-slate-950 sm:text-2xl">{editing ? `ویرایش ${title}` : `ایجاد ${title}`}</h1>
              <p className="mt-2 text-sm leading-6 text-slate-500">{editing ? `مشخصات ${title} را بررسی و به‌روزرسانی کنید.` : `اطلاعات ${title} را برای استفاده در اسناد بازرگانی ثبت کنید.`}</p>
            </div>
          </div>
          <button type="button" disabled={busy} onClick={() => navigate(`/${collection}/list`)} className="inline-flex items-center justify-center gap-2 self-start rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"><ArrowRight aria-hidden="true" size={17} />فهرست {plural}</button>
        </header>

        {loading ? (
          <div role="status" className="rounded-2xl border border-slate-200 bg-white p-6">
            <p className="mb-6 flex items-center gap-2 text-sm text-slate-600"><Loader2 aria-hidden="true" size={18} className="animate-spin" />در حال دریافت اطلاعات {title}…</p>
            <div aria-hidden="true" className="grid animate-pulse grid-cols-1 gap-5 sm:grid-cols-2">{[0, 1, 2, 3].map((item) => <div key={item} className="h-16 rounded-xl bg-slate-100" />)}</div>
          </div>
        ) : loadError ? (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-white p-6 text-sm text-slate-700">
            <AlertCircle aria-hidden="true" className="mb-3 text-rose-600" size={24} /><p>{loadError}</p>
            <button type="button" onClick={() => setRetry((value) => value + 1)} className="mt-4 rounded-lg border border-slate-300 px-4 py-2 font-medium hover:bg-slate-50">تلاش مجدد</button>
          </div>
        ) : (
          <form ref={formRef} onSubmit={submit} noValidate aria-busy={busy}>
            <fieldset disabled={busy} className="min-w-0">
              <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_280px]">
                <div className="min-w-0 space-y-5">
                  {errors.general && <div role="alert" tabIndex={-1} className="flex gap-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm leading-6 text-rose-800"><AlertCircle aria-hidden="true" size={20} className="mt-0.5 shrink-0" /><p className="min-w-0 break-words">{errors.general}</p></div>}
                  {config.sections.map((section) => <PartySection key={section.id} section={section} values={values} savedFiles={savedFiles} errors={errors.fields} update={update} clearFile={(name) => {
                    const input = formRef.current?.elements.namedItem(name);
                    if (input) input.value = "";
                    setValues((current) => ({ ...current, [name]: null }));
                    setErrors((current) => ({ ...current, fields: { ...current.fields, [name]: "" } }));
                  }} />)}
                  <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                    <p role="status" className={`flex items-center gap-2 text-sm ${saved ? "text-emerald-700" : "text-slate-500"}`}>
                      {saved ? <><CheckCircle2 aria-hidden="true" size={18} />تغییرات با موفقیت ذخیره شد.</> : saving ? <><Loader2 aria-hidden="true" size={17} className="animate-spin" />در حال ذخیره اطلاعات…</> : <><span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${dirty ? "bg-amber-400" : "bg-slate-300"}`} />{dirty ? "تغییرات ذخیره‌نشده دارید" : editing ? "اطلاعات به‌روز است" : "فیلدهای ستاره‌دار الزامی‌اند"}</>}
                    </p>
                    <button type="submit" disabled={busy || (editing && !dirty)} className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-50">
                      {saving ? <Loader2 aria-hidden="true" size={18} className="animate-spin" /> : <Save aria-hidden="true" size={18} />}
                      {saving ? "در حال ذخیره…" : editing ? "ذخیره تغییرات" : `ایجاد ${title}`}
                    </button>
                  </div>
                </div>

                <aside className="min-w-0 space-y-4 lg:sticky lg:top-24">
                  <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white lg:block">
                    <div className="border-b border-slate-100 bg-slate-50/70 p-5">
                      <span className="mb-4 flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white text-slate-500">{type === "seller" ? <ImagePreview file={values.seller_logo} existing={savedFiles.seller_logo} label="لوگوی فروشنده" /> : <PartyIcon aria-hidden="true" size={26} />}</span>
                      <p className="text-xs font-medium text-blue-700">{title}</p>
                      <h2 className="mt-2 break-words text-base font-bold leading-7 text-slate-900"><bdi>{displayName}</bdi></h2>
                      {editing && <p className="mt-2 text-xs text-slate-500">شناسه: <bdi dir="ltr">{recordId}</bdi></p>}
                    </div>
                    <nav aria-label="بخش‌های فرم" className="space-y-1 p-3">{config.sections.map((section) => {
                      const Icon = section.icon;
                      return <a key={section.id} href={`#party-${section.id}`} className="flex items-center justify-between gap-2 rounded-lg px-3 py-3 text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-700"><span className="flex items-center gap-2"><Icon aria-hidden="true" size={17} />{section.title}</span><ChevronLeft aria-hidden="true" size={15} /></a>;
                    })}</nav>
                    <p className="border-t border-slate-100 px-5 py-4 text-xs leading-6 text-slate-500">نام و نشانی را مطابق اسناد تجاری وارد کنید. {type === "seller" ? "اطلاعات بانکی و تصاویر فروشنده اختیاری‌اند." : "شماره کارت بازرگانی را با حفظ صفرهای ابتدایی وارد کنید."}</p>
                  </div>
                  {editing && <div className="rounded-xl border border-slate-200 bg-white p-4"><h2 className="text-sm font-semibold text-slate-800">حذف {title}</h2><p className="mt-2 text-xs leading-6 text-slate-500">برای حذف این {title} از فهرست، از گزینه زیر استفاده کنید.</p><button type="button" onClick={() => setDeleteOpen(true)} className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-rose-200 px-3 py-2.5 text-sm font-medium text-rose-700 hover:bg-rose-50"><Trash2 aria-hidden="true" size={16} />حذف {title}</button></div>}
                </aside>
              </div>
            </fieldset>
          </form>
        )}
      </div>
      {deleteOpen && <DeletePartyDialog title={title} name={displayName} deleting={deleting} onCancel={() => { if (!pending.current) setDeleteOpen(false); }} onDelete={deleteRecord} />}
    </div>
  );
}

function PartySection({ section, values, savedFiles, errors, update, clearFile }) {
  const Icon = section.icon;
  return (
    <section id={`party-${section.id}`} aria-labelledby={`party-${section.id}-title`} className="scroll-mt-28 overflow-hidden rounded-2xl border border-slate-200 bg-white">
      <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-5 sm:px-6">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-700"><Icon aria-hidden="true" size={19} /></span>
        <div><h2 id={`party-${section.id}-title`} className="text-base font-bold text-slate-900">{section.title}</h2><p className="mt-1 text-xs leading-6 text-slate-500">{section.description}</p></div>
      </div>
      <div className="grid min-w-0 grid-cols-1 gap-x-5 gap-y-6 p-5 sm:grid-cols-2 sm:p-6">
        {section.fields.map((field) => {
          const error = errors[field.name];
          const props = {
            id: field.name, name: field.name, required: field.required,
            maxLength: field.maxLength, autoComplete: field.autoComplete,
            dir: field.dir || (values[field.name] ? "auto" : "rtl"), inputMode: field.inputMode, placeholder: field.placeholder,
            onChange: (event) => update(field, event),
            "aria-invalid": Boolean(error),
            "aria-describedby": error ? `${field.name}-error` : field.hint ? `${field.name}-hint` : undefined,
            className: `w-full min-w-0 rounded-lg border bg-slate-50/60 px-3.5 py-3 text-sm leading-6 text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-blue-100 disabled:cursor-wait ${error ? "border-rose-400" : "border-slate-200 hover:border-slate-300 focus:border-blue-500"}`,
          };
          return <div key={field.name} className={`min-w-0 space-y-2 ${field.wide ? "sm:col-span-2" : ""}`}>
            <label htmlFor={field.name} className="flex items-center gap-1.5 text-sm font-medium text-slate-700">{field.label}{field.required ? <span className="text-rose-600" aria-label="الزامی">*</span> : <span className="text-xs font-normal text-slate-400">(اختیاری)</span>}</label>
            {field.type === "file" ? (
              <div className={`rounded-xl border border-dashed p-4 ${error ? "border-rose-300 bg-rose-50/30" : "border-slate-300 bg-slate-50/60"}`}>
                <div className="mb-4 flex h-28 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white"><ImagePreview file={values[field.name]} existing={savedFiles[field.name]} label={field.label} /></div>
                <label className="relative flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-700 hover:border-blue-300 focus-within:ring-2 focus-within:ring-blue-500"><UploadCloud aria-hidden="true" size={17} />{values[field.name] || savedFiles[field.name] ? "انتخاب تصویر جایگزین" : "انتخاب تصویر"}<input {...props} type="file" accept="image/*" className="absolute inset-0 h-full w-full cursor-pointer opacity-0" /></label>
                {values[field.name] ? <div className="mt-3 flex min-w-0 items-start gap-2 text-xs text-slate-600"><Check aria-hidden="true" size={15} className="shrink-0 text-blue-600" /><span className="min-w-0 flex-1 break-all"><bdi>{values[field.name].name}</bdi></span><button type="button" aria-label={`لغو انتخاب ${field.label}`} onClick={() => clearFile(field.name)} className="shrink-0 rounded p-0.5 text-slate-500 hover:bg-slate-200"><X aria-hidden="true" size={15} /></button></div> : savedFiles[field.name] ? <a href={savedFiles[field.name]} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex text-xs font-medium text-blue-700 underline underline-offset-4">مشاهده تصویر فعلی</a> : <p className="mt-3 text-xs text-slate-500">تصویری انتخاب نشده است.</p>}
              </div>
            ) : field.multiline ? <textarea {...props} rows={3} value={values[field.name]} className={`${props.className} resize-y`} /> : <input {...props} type={field.type || "text"} value={values[field.name]} />}
            {error ? <p id={`${field.name}-error`} className="text-xs leading-6 text-rose-700">{error}</p> : field.hint && <p id={`${field.name}-hint`} className="text-xs leading-6 text-slate-500">{field.hint}</p>}
          </div>;
        })}
      </div>
    </section>
  );
}

function ImagePreview({ file, existing, label }) {
  const [source, setSource] = useState(existing || "");
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
    if (!(file instanceof File)) {
      setSource(existing || "");
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setSource(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file, existing]);
  return source && !failed
    ? <img src={source} alt={label} onError={() => setFailed(true)} className="h-full w-full object-contain p-2" />
    : <ImagePlus aria-hidden="true" size={28} className="text-slate-300" />;
}

function DeletePartyDialog({ title, name, deleting, onCancel, onDelete }) {
  const panel = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    panel.current?.querySelector("button")?.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (previous?.isConnected) previous.focus();
    };
  }, []);
  return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/45 p-4" dir="rtl" onMouseDown={(event) => { if (event.target === event.currentTarget && !deleting) onCancel(); }}>
    <div ref={panel} role="alertdialog" aria-modal="true" aria-labelledby="delete-party-title" aria-describedby="delete-party-description" aria-busy={deleting} className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onKeyDown={(event) => {
      if (event.key === "Escape" && !deleting) onCancel();
      if (event.key === "Tab") {
        const buttons = panel.current.querySelectorAll("button:not(:disabled)");
        if (!buttons.length) { event.preventDefault(); return; }
        const target = event.shiftKey ? buttons[buttons.length - 1] : buttons[0];
        if (document.activeElement === (event.shiftKey ? buttons[0] : buttons[buttons.length - 1])) { event.preventDefault(); target.focus(); }
      }
    }}>
      <span className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-rose-50 text-rose-600"><Trash2 aria-hidden="true" size={21} /></span>
      <h2 id="delete-party-title" className="text-lg font-bold text-slate-900">حذف {title}</h2>
      <p id="delete-party-description" className="mt-3 break-words text-sm leading-7 text-slate-600">{title} «<bdi>{name}</bdi>» حذف شود؟ حذف این رکورد می‌تواند اسناد وابسته را نیز حذف کند. این اقدام قابل بازگشت نیست.</p>
      <div className="mt-6 flex flex-wrap justify-end gap-3">
        <button type="button" disabled={deleting} onClick={onCancel} className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm text-slate-700 disabled:opacity-50">انصراف</button>
        <button type="button" disabled={deleting} onClick={onDelete} className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-rose-700 disabled:opacity-50">{deleting && <Loader2 aria-hidden="true" size={16} className="animate-spin" />}{deleting ? "در حال حذف…" : `حذف ${title}`}</button>
      </div>
    </div>
  </div>;
}
